import type { VideoCategoryShallow } from "@brookbush/types";

export const findVideoCategoryPathBySlug = (
	categories: VideoCategoryShallow[],
	slugPath: string[],
): VideoCategoryShallow[] | null => {
	const findPath = (
		cats: VideoCategoryShallow[],
		remainingSlugs: string[],
		currentPath: VideoCategoryShallow[] = [],
	): VideoCategoryShallow[] | null => {
		if (remainingSlugs.length === 0) {
			return currentPath;
		}

		const [currentSlug, ...restSlugs] = remainingSlugs;

		for (const category of cats) {
			if (category.slug === currentSlug) {
				const newPath = [...currentPath, category];
				if (restSlugs.length === 0) {
					return newPath;
				}
				if (category.subCategories) {
					const subPath = findPath(category.subCategories, restSlugs, newPath);
					if (subPath) {
						return subPath;
					}
				}
			}
		}
		return null;
	};

	return findPath(categories, slugPath);
};

export const getCategoryPath = (
	shallowCategories: VideoCategoryShallow[],
	category: VideoCategoryShallow,
	parentSlugs: string[] = [],
): string[] => {
	const slugPath = [...parentSlugs, category.slug];
	const fullPath = findVideoCategoryPathBySlug(shallowCategories, slugPath);
	return fullPath ? fullPath.map((cat) => cat.slug) : [];
};

export const getCategoryPathByCategory = (
	shallowCategories: VideoCategoryShallow[],
	category: VideoCategoryShallow,
	path: VideoCategoryShallow[] = [],
): VideoCategoryShallow[] => {
	// Base case: if the category is found in the current level
	const foundCategory = shallowCategories.find(
		(shallowCategory) => shallowCategory.slug === category.slug,
	);

	if (foundCategory) {
		return [...path, foundCategory]; // Return path with the current category
	}

	// Recursive case: search in subcategories
	for (const shallowCategory of shallowCategories) {
		if (
			shallowCategory.subCategories &&
			shallowCategory.subCategories.length > 0
		) {
			const subPath = getCategoryPathByCategory(
				shallowCategory.subCategories,
				category,
				[...path, shallowCategory], // Accumulate the current category in the path
			);

			if (subPath.length > 0) {
				return subPath; // Return the path if found
			}
		}
	}

	// If not found, return an empty array
	return [];
};
